import { WAREHOUSE_DISPLAY_NAMES } from '@elementary/common-ui/constants/warehouses';
import { WarehouseType } from '@elementary/common-ui/types';
import {
  AthenaAuthMethod,
  BigQueryAuthMethod,
  DatabricksAuthMethod,
  DatabricksStorageAccessType,
  SnowflakeAuthMethod,
} from 'shared/types';
import { DREMIO_HOST_OPTIONS, ENVIRONMENT_TYPE_OPTIONS } from '../constants';
import { EnvironmentFormFieldSpec } from './types';

export const USER_ONLY_FIELD_REASON =
  'Secrets and credential files are entered by the user directly in the form. Ask the user to fill it in; never ask them to paste it into the chat.';

export const MAX_PREVIEW_VALUE_LENGTH = 80;

export const USER_ONLY_FIELD_PREVIEW = 'Not applied, enter it in the form';

export const UNKNOWN_FIELD_PREVIEW = 'Unknown field, not applied';

export const ASSET_SOURCE_PREVIEW_LABEL = 'Asset source';

export const SSH_TUNNEL_NOTE =
  'SSH tunnel setup is done together with the Elementary team. If the user asks how to set up the tunnel or what to configure on their side, point them to Elementary support instead of guessing the steps.';

export const CONNECTION_STEP_DESCRIPTION =
  'Environment name and type, the data warehouse, and the credentials Elementary uses to connect to it.';

export const CONTENT_STEP_DESCRIPTION =
  'Asset sources Elementary monitors. A dbt project source reads the tables the Elementary dbt package writes (the Elementary schema, usually <schema>_elementary), so the package must already be installed and run. A warehouse source monitors tables directly, filtered by database and optional schemas (no schemas means all schemas in that database).';

const SSH_TUNNEL_FIELDS: EnvironmentFormFieldSpec[] = [
  {
    name: 'sshEnabled',
    label: 'Connect via SSH tunnel',
    kind: 'boolean',
    skipDirty: true,
  },
  {
    name: 'sshHost',
    label: 'SSH host',
    kind: 'text',
    required: true,
    visibleWhen: { field: 'sshEnabled', values: [true] },
  },
  {
    name: 'sshPort',
    label: 'SSH port',
    kind: 'port',
    required: true,
    storesNumber: true,
    visibleWhen: { field: 'sshEnabled', values: [true] },
  },
  {
    name: 'sshUsername',
    label: 'SSH username',
    kind: 'text',
    required: true,
    visibleWhen: { field: 'sshEnabled', values: [true] },
  },
];

export const ENVIRONMENT_DETAILS_FIELDS: EnvironmentFormFieldSpec[] = [
  {
    name: 'environmentName',
    label: 'Environment name',
    kind: 'text',
    required: true,
  },
  {
    name: 'environmentType',
    label: 'Environment type',
    kind: 'select',
    options: ENVIRONMENT_TYPE_OPTIONS,
  },
];

const RECOMMENDED_NOTE = 'Recommended';
const DEPRECATED_NOTE = 'Deprecated, not recommended';
const LEGACY_NOTE = 'Legacy';

const AWS_AUTH_LABELS = {
  secretAccessKey: 'Secret access key',
  roleArn: 'AWS role ARN',
};

export const WAREHOUSE_FIELDS: Record<
  WarehouseType,
  EnvironmentFormFieldSpec[]
> = {
  snowflake: [
    {
      name: 'snowflakeAccount',
      label: 'Account',
      kind: 'text',
      required: true,
    },
    {
      name: 'snowflakeWarehouse',
      label: 'Warehouse',
      kind: 'text',
      required: true,
    },
    { name: 'snowflakeUserRole', label: 'User role', kind: 'text' },
    {
      name: 'snowflakeAuthMethod',
      label: 'Authentication method',
      kind: 'select',
      skipDirty: true,
      options: [
        {
          value: SnowflakeAuthMethod.KEY_PAIR,
          label: 'Key pair',
          note: RECOMMENDED_NOTE,
        },
        {
          value: SnowflakeAuthMethod.PASSWORD,
          label: 'Username & password (deprecated)',
          note: DEPRECATED_NOTE,
        },
      ],
    },
    { name: 'snowflakeUser', label: 'User', kind: 'text', required: true },
    {
      name: 'snowflakePrivateKey',
      label: 'Private key',
      kind: 'secret',
      required: true,
      visibleWhen: {
        field: 'snowflakeAuthMethod',
        values: [SnowflakeAuthMethod.KEY_PAIR],
      },
    },
    {
      name: 'snowflakePrivateKeyPassphrase',
      label: 'Private key passphrase',
      kind: 'secret',
      visibleWhen: {
        field: 'snowflakeAuthMethod',
        values: [SnowflakeAuthMethod.KEY_PAIR],
      },
    },
    {
      name: 'snowflakePassword',
      label: 'Password',
      kind: 'secret',
      required: true,
      visibleWhen: {
        field: 'snowflakeAuthMethod',
        values: [SnowflakeAuthMethod.PASSWORD],
      },
    },
  ],
  redshift: [
    { name: 'redshiftHost', label: 'Host', kind: 'text', required: true },
    { name: 'redshiftPort', label: 'Port', kind: 'port', required: true },
    { name: 'redshiftUsername', label: 'User', kind: 'text', required: true },
    {
      name: 'redshiftPassword',
      label: 'Password',
      kind: 'secret',
      required: true,
    },
    ...SSH_TUNNEL_FIELDS,
  ],
  bigquery: [
    {
      name: 'bigqueryLocation',
      label: 'Location',
      kind: 'text',
      required: true,
    },
    {
      name: 'bigqueryAuthMethod',
      label: 'Authentication method',
      kind: 'select',
      skipDirty: true,
      options: [
        { value: BigQueryAuthMethod.SERVICE_ACCOUNT, label: 'Service account' },
        {
          value: BigQueryAuthMethod.WIF,
          label: 'Workload Identity Federation',
        },
      ],
    },
    {
      name: 'bigqueryAccountFile',
      label: 'Service account file',
      kind: 'file',
      required: true,
      visibleWhen: {
        field: 'bigqueryAuthMethod',
        values: [BigQueryAuthMethod.SERVICE_ACCOUNT],
      },
    },
    {
      name: 'bigqueryWifFile',
      label: 'WIF credential file',
      kind: 'file',
      required: true,
      visibleWhen: {
        field: 'bigqueryAuthMethod',
        values: [BigQueryAuthMethod.WIF],
      },
    },
  ],
  databricks: [
    {
      name: 'databricksHost',
      label: 'Server host',
      kind: 'text',
      required: true,
    },
    {
      name: 'databricksHttpPath',
      label: 'HTTP path',
      kind: 'text',
      required: true,
    },
    {
      name: 'databricksAuthMethod',
      label: 'Authentication method',
      kind: 'select',
      options: [
        {
          value: DatabricksAuthMethod.OAUTH,
          label: 'OAuth (M2M)',
          note: RECOMMENDED_NOTE,
        },
        {
          value: DatabricksAuthMethod.TOKEN,
          label: 'Access token (legacy)',
          note: LEGACY_NOTE,
        },
      ],
    },
    {
      name: 'databricksClientId',
      label: 'Client ID',
      kind: 'text',
      required: true,
      visibleWhen: {
        field: 'databricksAuthMethod',
        values: [DatabricksAuthMethod.OAUTH],
      },
    },
    {
      name: 'databricksClientSecret',
      label: 'Client secret',
      kind: 'secret',
      required: true,
      visibleWhen: {
        field: 'databricksAuthMethod',
        values: [DatabricksAuthMethod.OAUTH],
      },
    },
    {
      name: 'databricksAccessToken',
      label: 'Access token',
      kind: 'secret',
      required: true,
      visibleWhen: {
        field: 'databricksAuthMethod',
        values: [DatabricksAuthMethod.TOKEN],
      },
    },
    {
      name: 'databricksStorageAccessType',
      label: 'Storage access method',
      kind: 'select',
      skipDirty: true,
      allowsNone: true,
      options: [
        {
          value: DatabricksStorageAccessType.CREDENTIALS_VENDING,
          label: 'Credentials vending',
        },
        {
          value: DatabricksStorageAccessType.ACCESS_KEYS,
          label: `Direct storage access, ${AWS_AUTH_LABELS.secretAccessKey}`,
        },
        {
          value: DatabricksStorageAccessType.ROLE_ARN,
          label: `Direct storage access, ${AWS_AUTH_LABELS.roleArn}`,
        },
      ],
    },
    {
      name: 'databricksStorageAccessKeyId',
      label: 'Storage access key ID',
      kind: 'secret',
      required: true,
      visibleWhen: {
        field: 'databricksStorageAccessType',
        values: [DatabricksStorageAccessType.ACCESS_KEYS],
      },
    },
    {
      name: 'databricksStorageSecretAccessKey',
      label: 'Storage secret access key',
      kind: 'secret',
      required: true,
      visibleWhen: {
        field: 'databricksStorageAccessType',
        values: [DatabricksStorageAccessType.ACCESS_KEYS],
      },
    },
    {
      name: 'databricksStorageSessionToken',
      label: 'Storage session token',
      kind: 'secret',
      visibleWhen: {
        field: 'databricksStorageAccessType',
        values: [DatabricksStorageAccessType.ACCESS_KEYS],
      },
    },
    {
      name: 'databricksStorageRoleArn',
      label: 'Storage AWS role ARN',
      kind: 'text',
      required: true,
      visibleWhen: {
        field: 'databricksStorageAccessType',
        values: [DatabricksStorageAccessType.ROLE_ARN],
      },
    },
    {
      name: 'databricksStorageExternalId',
      label: 'Storage external ID',
      kind: 'secret',
      visibleWhen: {
        field: 'databricksStorageAccessType',
        values: [DatabricksStorageAccessType.ROLE_ARN],
      },
    },
  ],
  postgres: [
    { name: 'postgresHost', label: 'Host', kind: 'text', required: true },
    { name: 'postgresPort', label: 'Port', kind: 'port', required: true },
    { name: 'postgresUser', label: 'User', kind: 'text', required: true },
    {
      name: 'postgresPassword',
      label: 'Password',
      kind: 'secret',
      required: true,
    },
    ...SSH_TUNNEL_FIELDS,
  ],
  athena: [
    { name: 'athenaRegion', label: 'Region', kind: 'text', required: true },
    {
      name: 'athenaS3StagingDir',
      label: 'S3 staging directory',
      kind: 'text',
      required: true,
    },
    { name: 'athenaWorkgroup', label: 'Workgroup', kind: 'text' },
    {
      name: 'athenaAuthMethod',
      label: 'Authentication method',
      kind: 'select',
      skipDirty: true,
      options: [
        {
          value: AthenaAuthMethod.SECRET_ACCESS_KEY,
          label: AWS_AUTH_LABELS.secretAccessKey,
        },
        { value: AthenaAuthMethod.ROLE_ARN, label: AWS_AUTH_LABELS.roleArn },
      ],
    },
    {
      name: 'athenaAccessKeyId',
      label: 'Access key ID',
      kind: 'secret',
      required: true,
      visibleWhen: {
        field: 'athenaAuthMethod',
        values: [AthenaAuthMethod.SECRET_ACCESS_KEY],
      },
    },
    {
      name: 'athenaSecretAccessKey',
      label: 'Secret access key',
      kind: 'secret',
      required: true,
      visibleWhen: {
        field: 'athenaAuthMethod',
        values: [AthenaAuthMethod.SECRET_ACCESS_KEY],
      },
    },
    {
      name: 'athenaSessionToken',
      label: 'Session token',
      kind: 'secret',
      visibleWhen: {
        field: 'athenaAuthMethod',
        values: [AthenaAuthMethod.SECRET_ACCESS_KEY],
      },
    },
    {
      name: 'athenaRoleArn',
      label: 'AWS role ARN',
      kind: 'text',
      required: true,
      visibleWhen: {
        field: 'athenaAuthMethod',
        values: [AthenaAuthMethod.ROLE_ARN],
      },
    },
    {
      name: 'athenaExternalId',
      label: 'External ID',
      kind: 'secret',
      visibleWhen: {
        field: 'athenaAuthMethod',
        values: [AthenaAuthMethod.ROLE_ARN],
      },
    },
  ],
  clickhouse: [
    { name: 'clickhouseHost', label: 'Host', kind: 'text', required: true },
    { name: 'clickhousePort', label: 'Port', kind: 'port', required: true },
    { name: 'clickhouseUser', label: 'User', kind: 'text', required: true },
    {
      name: 'clickhousePassword',
      label: 'Password',
      kind: 'secret',
      required: true,
    },
  ],
  dremio: [
    {
      name: 'dremioHost',
      label: 'Host',
      kind: 'select',
      required: true,
      options: DREMIO_HOST_OPTIONS,
    },
    {
      name: 'dremioProjectId',
      label: 'Project ID',
      kind: 'text',
      required: true,
    },
    { name: 'dremioUser', label: 'User', kind: 'text', required: true },
    { name: 'dremioToken', label: 'Token', kind: 'secret', required: true },
  ],
  fabric: [
    { name: 'fabricServer', label: 'Server', kind: 'text', required: true },
    {
      name: 'fabricPort',
      label: 'Port',
      kind: 'port',
      required: true,
      storesNumber: true,
    },
    {
      name: 'fabricTenantId',
      label: 'Tenant ID',
      kind: 'text',
      required: true,
    },
    {
      name: 'fabricClientId',
      label: 'Client ID',
      kind: 'text',
      required: true,
    },
    {
      name: 'fabricClientSecret',
      label: 'Client secret',
      kind: 'secret',
      required: true,
    },
  ],
};

export const WAREHOUSE_TYPE_FIELD: EnvironmentFormFieldSpec = {
  name: 'warehouseType',
  label: 'Data warehouse',
  kind: 'select',
  required: true,
  skipDirty: true,
  options: Object.entries(WAREHOUSE_DISPLAY_NAMES).map(([value, label]) => ({
    value,
    label,
  })),
};

export const ASSET_SOURCES_FIELD: EnvironmentFormFieldSpec = {
  name: 'assetSources',
  label: 'Asset sources',
  kind: 'assetSources',
  required: true,
};
